import React from 'react';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';
import {
  ShoppingBag,
  Flame,
  QrCode,
  UtensilsCrossed,
  Sun,
  Moon,
  LogOut,
  ChefHat,
  UserCheck,
  Shield,
  History
} from 'lucide-react';
import { formatEuro } from '../utils/format';

export const Header: React.FC = () => {
  const {
    currentView,
    setCurrentView,
    currentTable,
    cartItemCount,
    cartTotal,
    setIsCartDrawerOpen,
    theme,
    toggleTheme,
    currentStaffRole,
    activeStaffUser,
    logoutStaff,
    openStaffLogin,
    openOrderHistory
  } = useApp();

  const { lang, setLang, t } = useLanguage();

  return (
    <header className="app-header no-print">
      {/* Brand (Non-navigable: table diners stay strictly on food order page) */}
      <div
        className="header-brand"
        style={{ cursor: 'default', userSelect: 'none', flexShrink: 0 }}
      >
        <div className="brand-icon-wrap">
          <Flame size={18} />
        </div>
        <div className="brand-info">
          <div className="brand-title">The Oven Story</div>
          <div className="brand-domain desktop-only">Helsinki · Pöytätilaus</div>
        </div>
      </div>

      {/* Navigation / Switcher & Actions */}
      <div className="header-actions">
        {/* CUSTOMER & ORDER HISTORY MODE HEADER */}
        {(currentView === 'customer' || currentView === 'order-history') && (
          <>
            {/* Fixed Locked Table Badge */}
            <div
              className="table-pill"
              title="Pöytäsi on lukittu skannatun QR-koodin mukaan"
              style={{
                cursor: 'default',
                flexShrink: 0,
                background: 'rgba(224, 90, 54, 0.12)',
                borderColor: 'rgba(224, 90, 54, 0.35)',
                color: 'var(--accent-copper)',
                fontWeight: 700
              }}
            >
              <QrCode size={13} />
              <span>
                {t('table')} {currentTable?.table_number || '1'}
              </span>
            </div>

            {/* Dark / Light Mode Toggle */}
            <button
              className="mode-toggle-btn"
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
            >
              {theme === 'dark' ? (
                <Sun size={15} color="var(--accent-amber)" />
              ) : (
                <Moon size={15} color="var(--accent-copper)" />
              )}
            </button>

            {/* Language Selector */}
            <div style={{ display: 'flex', gap: '2px' }}>
              <button
                className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                onClick={() => setLang('en')}
                title="English"
              >
                EN
              </button>
              <button
                className={`lang-btn ${lang === 'fi' ? 'active' : ''}`}
                onClick={() => setLang('fi')}
                title="Suomi"
              >
                FI
              </button>
              <button
                className={`lang-btn ${lang === 'sv' ? 'active' : ''}`}
                onClick={() => setLang('sv')}
                title="Svenska"
              >
                SV
              </button>
            </div>
          </>
        )}

        {/* STAFF MODE HEADER */}
        {currentView.startsWith('staff-') && (
          <>
            {currentStaffRole && activeStaffUser ? (
              <>
                {/* Active Staff Role Badge */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    background: 'var(--bg-card)',
                    border: '1px solid var(--border-medium)',
                    padding: '4px 10px',
                    borderRadius: 'var(--radius-full)',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: currentStaffRole === 'kitchen' ? 'var(--accent-amber)' : currentStaffRole === 'manager' ? 'var(--accent-copper)' : 'var(--accent-emerald)'
                  }}
                >
                  {currentStaffRole === 'kitchen' && <ChefHat size={14} />}
                  {currentStaffRole === 'manager' && <UserCheck size={14} />}
                  {currentStaffRole === 'admin' && <Shield size={14} />}
                  <span>
                    {activeStaffUser.name ? `${activeStaffUser.name} (${currentStaffRole.toUpperCase()})` : currentStaffRole === 'kitchen' ? 'Keittiö' : currentStaffRole === 'manager' ? 'Päällikkö' : 'Admin'}
                  </span>
                </div>

                {/* Dark / Light Toggle */}
                <button
                  className="mode-toggle-btn"
                  onClick={toggleTheme}
                >
                  {theme === 'dark' ? <Sun size={15} color="var(--accent-amber)" /> : <Moon size={15} color="var(--accent-copper)" />}
                </button>

                {/* Language Selector */}
                <div style={{ display: 'flex', gap: '2px' }}>
                  <button
                    className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
                    onClick={() => setLang('en')}
                  >
                    EN
                  </button>
                  <button
                    className={`lang-btn ${lang === 'fi' ? 'active' : ''}`}
                    onClick={() => setLang('fi')}
                  >
                    FI
                  </button>
                </div>

                {/* Logout Staff Button */}
                <button
                  className="btn-secondary"
                  onClick={logoutStaff}
                  style={{ padding: '5px 10px', fontSize: '0.75rem', borderRadius: 'var(--radius-full)' }}
                  title="Kirjaudu ulos / Switch to customer mode"
                >
                  <LogOut size={14} />
                  <span className="desktop-only">Kirjaudu ulos</span>
                </button>
              </>
            ) : (
              <>
                <button
                  className="btn-secondary"
                  onClick={() => setCurrentView('customer')}
                  style={{ padding: '5px 12px', fontSize: '0.78rem', borderRadius: 'var(--radius-full)' }}
                >
                  Asiakasvalikko / Customer Menu
                </button>
              </>
            )}
          </>
        )}
      </div>
    </header>
  );
};
